<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';
import SchedulerAPI from './schedulerApi';
import {
  browserTimezone,
  defaultScheduleValue,
  isoToLocalInput,
  localInputToIso,
  windowHintKey,
} from './helpers';
import {
  emptyRecurrence,
  fromApiRecurrence,
  toApiRecurrence,
} from './recurrenceHelpers';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WhatsappTemplates from 'dashboard/components/widgets/conversation/WhatsappTemplates/Modal.vue';
import WindowHint from 'dashboard/wasapi/common/WindowHint.vue';

const emit = defineEmits(['saved']);

const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');
const inboxGetter = useMapGetter('inboxes/getInbox');

const dialogRef = ref(null);
const editingId = ref(null);
const isSaving = ref(false);
const showTemplates = ref(false);
const contactQuery = ref('');
const contactResults = ref([]);
const isUploading = ref(false);
const fileInputRef = ref(null);
let searchTimer = null;

const timezone = browserTimezone();
const form = reactive({
  name: '',
  notes: '',
  content: '',
  templateParams: null,
  contentLocked: false,
  scheduledAtLocal: defaultScheduleValue(),
  inboxId: '',
  contacts: [],
  cancelOnReply: false,
  recurrence: emptyRecurrence(),
  attachments: [], // { name, blobId }
});

const inboxOptions = computed(() =>
  (inboxes.value || []).map(i => ({ value: i.id, label: i.name }))
);
const isWhatsApp = computed(
  () => inboxGetter.value(form.inboxId)?.channel_type === 'Channel::Whatsapp'
);
// WhatsApp: template picked = deliverable, hide the hint. Meta/TikTok: no escape, always warn.
const windowHint = computed(() => {
  const key = windowHintKey(inboxGetter.value(form.inboxId));
  return key === 'WINDOW_HINT' && form.templateParams ? null : key;
});
const recurrenceTypeOptions = computed(() =>
  ['once', 'daily', 'weekly', 'monthly', 'yearly'].map(type => ({
    value: type,
    label: t(`WASAPI_SCHEDULER.RECURRENCE.${type.toUpperCase()}`),
  }))
);
const weekdays = computed(() =>
  [0, 1, 2, 3, 4, 5, 6].map(day => ({
    value: day,
    label: t(`WASAPI_SCHEDULER.RECURRENCE.WEEKDAYS.${day}`),
  }))
);
const VARIABLES = [
  '{{contact.name}}',
  '{{contact.first_name}}',
  '{{contact.phone_number}}',
  '{{contact.email}}',
  '{{agent.name}}',
];

const canSave = computed(
  () =>
    !!form.inboxId &&
    (editingId.value || form.contacts.length > 0) &&
    !!form.scheduledAtLocal &&
    (form.content.trim() || form.templateParams || form.attachments.length) &&
    !isSaving.value
);

const open = (row = null) => {
  editingId.value = row?.id || null;
  Object.assign(form, {
    name: row?.name || '',
    notes: row?.notes || '',
    content: row?.content || '',
    templateParams: row?.template_params || null,
    contentLocked: !!row?.content_locked,
    scheduledAtLocal: row
      ? isoToLocalInput(row.scheduled_at)
      : defaultScheduleValue(),
    inboxId: row?.inbox?.id || inboxOptions.value[0]?.value || '',
    contacts: row?.contact ? [row.contact] : [],
    cancelOnReply: !!row?.cancel_on_reply,
    recurrence: fromApiRecurrence(row?.recurrence),
    attachments: [],
  });
  contactQuery.value = '';
  contactResults.value = [];
  dialogRef.value?.open();
};

const searchContacts = value => {
  contactQuery.value = value;
  clearTimeout(searchTimer);
  if (!value || value.length < 2) {
    contactResults.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    try {
      const { data } = await ContactAPI.search(value, 1, 'name');
      contactResults.value = (data.payload || []).filter(
        contact => !form.contacts.some(c => c.id === contact.id)
      );
    } catch {
      contactResults.value = [];
    }
  }, 300);
};

const addContact = contact => {
  form.contacts = [...form.contacts, contact];
  contactResults.value = contactResults.value.filter(c => c.id !== contact.id);
};
const removeContact = contact => {
  form.contacts = form.contacts.filter(c => c.id !== contact.id);
};

const toggleWeekday = day => {
  const list = form.recurrence.days_of_week;
  form.recurrence.days_of_week = list.includes(day)
    ? list.filter(d => d !== day)
    : [...list, day];
};

const insertVariable = variable => {
  form.content = `${form.content}${form.content.endsWith(' ') || !form.content ? '' : ' '}${variable}`;
};

const onTemplatePicked = ({ message, templateParams }) => {
  form.content = message || '';
  form.templateParams = templateParams || null;
  showTemplates.value = false;
};

const pickFile = () => fileInputRef.value?.click();
const onFile = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  isUploading.value = true;
  try {
    const { data } = await SchedulerAPI.uploadAttachment(file);
    form.attachments = [
      ...form.attachments,
      { name: file.name, blobId: data.blob_id },
    ];
  } catch {
    useAlert(t('WASAPI_SCHEDULER.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};

const save = async () => {
  if (!canSave.value) return;
  isSaving.value = true;
  const scheduledMessage = {
    name: form.name.trim() || null,
    notes: form.notes.trim() || null,
    content: form.content.trim(),
    template_params: form.templateParams,
    scheduled_at: localInputToIso(form.scheduledAtLocal),
    timezone,
    cancel_on_reply: form.cancelOnReply,
    recurrence: toApiRecurrence(form.recurrence),
  };
  try {
    if (editingId.value) {
      await SchedulerAPI.update(editingId.value, {
        scheduled_message: scheduledMessage,
        attachment_ids: form.attachments.map(a => a.blobId),
      });
    } else {
      await SchedulerAPI.create({
        scheduled_message: scheduledMessage,
        inbox_id: form.inboxId,
        contact_ids: form.contacts.map(c => c.id),
        attachment_ids: form.attachments.map(a => a.blobId),
      });
    }
    useAlert(t('WASAPI_SCHEDULED.SAVED'));
    emit('saved');
    dialogRef.value?.close();
  } catch (error) {
    const data = error?.response?.data || {};
    useAlert(
      data.error ||
        (data.errors && Object.values(data.errors).flat().join(', ')) ||
        t('WASAPI_SCHEDULED.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :title="
      t(
        editingId ? 'WASAPI_SCHEDULED.EDIT_TITLE' : 'WASAPI_SCHEDULER.NEW_TITLE'
      )
    "
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <!-- Left column: the message -->
      <div class="flex flex-col gap-4 min-w-0">
        <Input
          v-model="form.name"
          :label="t('WASAPI_SCHEDULER.FIELDS.NAME')"
          :placeholder="t('WASAPI_SCHEDULER.FIELDS.NAME_PLACEHOLDER')"
        />
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">{{
            t('WASAPI_FORWARD.INBOX')
          }}</span>
          <ComboBox
            v-model="form.inboxId"
            :options="inboxOptions"
            :disabled="!!editingId"
            class="w-full"
          />
        </div>

        <div v-if="!editingId" class="flex flex-col gap-2">
          <Input
            :model-value="contactQuery"
            :label="t('WASAPI_SCHEDULER.FIELDS.RECIPIENTS')"
            :placeholder="t('WASAPI_FORWARD.SEARCH_PLACEHOLDER')"
            @input="searchContacts($event.target.value)"
          />
          <div v-if="form.contacts.length" class="flex flex-wrap gap-2">
            <span
              v-for="contact in form.contacts"
              :key="contact.id"
              class="flex items-center gap-1.5 pl-1 pr-2 py-1 text-xs rounded-full bg-n-brand/10 text-n-slate-12"
            >
              <Avatar
                :name="contact.name"
                :src="contact.thumbnail"
                :size="18"
                rounded-full
              />
              {{ contact.name }}
              <button
                type="button"
                class="grid place-content-center size-4 rounded-full hover:bg-n-alpha-2"
                @click="removeContact(contact)"
              >
                <Icon icon="i-lucide-x" class="size-3" />
              </button>
            </span>
          </div>
          <ul
            v-if="contactResults.length"
            class="flex flex-col max-h-40 overflow-y-auto rounded-lg outline outline-1 outline-n-weak divide-y divide-n-weak"
          >
            <li v-for="contact in contactResults" :key="contact.id">
              <button
                type="button"
                class="flex items-center w-full gap-2 px-3 py-2 text-sm text-left hover:bg-n-alpha-1"
                @click="addContact(contact)"
              >
                <Avatar
                  :name="contact.name"
                  :src="contact.thumbnail"
                  :size="24"
                  rounded-full
                />
                <span class="truncate">
                  {{
                    [contact.name, contact.phone_number]
                      .filter(Boolean)
                      .join(' · ')
                  }}
                </span>
              </button>
            </li>
          </ul>
        </div>

        <div class="flex flex-col gap-2">
          <div
            v-if="form.templateParams"
            class="flex flex-col gap-2 p-3 rounded-xl outline outline-1 outline-n-weak bg-n-alpha-1"
          >
            <div class="flex items-center gap-2">
              <Icon icon="i-ph-whatsapp-logo" class="size-4 text-n-teal-11" />
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('WASAPI_SCHEDULED.TEMPLATE_BADGE') }} ·
                {{ form.templateParams.name }}
                <template v-if="form.templateParams.language">
                  ({{ form.templateParams.language }})
                </template>
              </span>
              <span class="flex-1" />
              <Button
                v-if="!form.contentLocked"
                v-tooltip="t('WASAPI_SCHEDULED.CLEAR_TEMPLATE')"
                icon="i-lucide-x"
                variant="ghost"
                color="slate"
                size="sm"
                @click="form.templateParams = null"
              />
            </div>
            <p class="text-sm whitespace-pre-wrap text-n-slate-12">
              {{ form.content }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ t('WASAPI_SCHEDULED.TEMPLATE_MODE_HINT') }}
            </p>
            <p v-if="form.contentLocked" class="text-xs text-n-amber-11">
              {{ t('WASAPI_SCHEDULED.TEMPLATE_LOCKED_HINT') }}
            </p>
          </div>
          <template v-else>
            <TextArea
              v-model="form.content"
              :placeholder="t('WASAPI_SCHEDULED.CONTENT_PLACEHOLDER')"
              class="w-full"
              auto-height
            />
            <div class="flex flex-wrap items-center gap-1.5">
              <span class="text-xs text-n-slate-10"
                >{{ t('WASAPI_SCHEDULER.VARIABLES') }}:</span
              >
              <button
                v-for="variable in VARIABLES"
                :key="variable"
                type="button"
                class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 font-mono"
                @click="insertVariable(variable)"
              >
                {{ variable }}
              </button>
            </div>
          </template>
          <div class="flex flex-wrap items-center gap-2">
            <Button
              v-if="isWhatsApp && !form.templateParams"
              :label="t('WASAPI_SCHEDULED.USE_TEMPLATE')"
              icon="i-ph-whatsapp-logo"
              variant="faded"
              color="slate"
              size="sm"
              @click="showTemplates = true"
            />
            <Button
              :label="t('WASAPI_SCHEDULER.ATTACH')"
              icon="i-lucide-paperclip"
              variant="faded"
              color="slate"
              size="sm"
              :is-loading="isUploading"
              @click="pickFile"
            />
            <input
              ref="fileInputRef"
              type="file"
              class="hidden"
              @change="onFile"
            />
            <span
              v-for="(file, index) in form.attachments"
              :key="file.blobId"
              class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-n-alpha-1 text-n-slate-11"
            >
              <Icon icon="i-lucide-paperclip" class="size-3" />{{ file.name }}
              <button type="button" @click="form.attachments.splice(index, 1)">
                <Icon icon="i-lucide-x" class="size-3" />
              </button>
            </span>
          </div>
          <WindowHint
            v-if="windowHint"
            :text="t(`WASAPI_SCHEDULED.${windowHint}`)"
          />
        </div>
      </div>

      <!-- Right column: when and how -->
      <div class="flex flex-col gap-4 min-w-0">
        <Input
          v-model="form.scheduledAtLocal"
          type="datetime-local"
          :label="t('WASAPI_SCHEDULED.WHEN')"
        />

        <div
          class="flex flex-col gap-3 p-3 rounded-xl outline outline-1 outline-n-weak"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="text-sm font-medium text-n-slate-12">{{
              t('WASAPI_SCHEDULER.RECURRENCE.TITLE')
            }}</span>
            <ComboBox
              v-model="form.recurrence.type"
              :options="recurrenceTypeOptions"
            />
          </div>
          <div
            v-if="form.recurrence.type === 'weekly'"
            class="flex flex-wrap gap-1.5"
          >
            <button
              v-for="day in weekdays"
              :key="day.value"
              type="button"
              class="px-2 py-1 text-xs rounded-full outline outline-1"
              :class="
                form.recurrence.days_of_week.includes(day.value)
                  ? 'outline-n-brand bg-n-brand/10 text-n-slate-12'
                  : 'outline-n-weak text-n-slate-11'
              "
              @click="toggleWeekday(day.value)"
            >
              {{ day.label }}
            </button>
          </div>
          <Input
            v-if="form.recurrence.type === 'monthly'"
            v-model="form.recurrence.day_of_month"
            type="number"
            min="1"
            max="31"
            :label="t('WASAPI_SCHEDULER.RECURRENCE.DAY_OF_MONTH')"
            class="w-32"
          />
          <div
            v-if="form.recurrence.type !== 'once'"
            class="grid grid-cols-2 gap-3"
          >
            <Input
              v-model="form.recurrence.end_date"
              type="date"
              :label="t('WASAPI_SCHEDULER.RECURRENCE.END_DATE')"
            />
            <Input
              v-model="form.recurrence.max_executions"
              type="number"
              min="1"
              :label="t('WASAPI_SCHEDULER.RECURRENCE.MAX')"
            />
          </div>
        </div>

        <label
          class="flex items-center justify-between gap-3 p-3 rounded-xl outline outline-1 outline-n-weak"
        >
          <span class="flex flex-col">
            <span class="text-sm text-n-slate-12">{{
              t('WASAPI_SCHEDULER.CANCEL_ON_REPLY')
            }}</span>
            <span class="text-xs text-n-slate-10">{{
              t('WASAPI_SCHEDULER.CANCEL_ON_REPLY_HELP')
            }}</span>
          </span>
          <Switch v-model="form.cancelOnReply" />
        </label>

        <TextArea
          v-model="form.notes"
          :placeholder="t('WASAPI_SCHEDULER.FIELDS.NOTES')"
          class="w-full"
          auto-height
        />
      </div>
    </div>
    <template #footer>
      <div class="flex items-center justify-end w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          @click="dialogRef.close()"
        />
        <Button
          :label="t('WASAPI_SCHEDULED.SAVE')"
          color="blue"
          icon="i-lucide-clock"
          :disabled="!canSave"
          :is-loading="isSaving"
          @click="save"
        />
      </div>
    </template>
    <WhatsappTemplates
      v-if="isWhatsApp"
      :inbox-id="Number(form.inboxId)"
      :show="showTemplates"
      @on-send="onTemplatePicked"
      @cancel="showTemplates = false"
      @update:show="showTemplates = $event"
    />
  </Dialog>
</template>
